import { useCallback, useState } from 'react';

/**
 * The $LEGATE contract address, under the hero call to action.
 *
 * It renders **nothing at all** until an address exists. A placeholder, a
 * "coming soon", or a zero address on a page that also asks for money is the
 * kind of detail people screenshot, so the absence is deliberate: no address,
 * no strip.
 *
 * The value arrives at build time from LEGATE_TOKEN_ADDRESS (see vite.config.ts,
 * which lifts it out of the repo-root .env). Nothing here calls the API — the
 * public site must render with the backend down.
 */

const EXPLORER = 'https://robinhoodchain.blockscout.com';

function isAddress(value: string): boolean {
  return /^0x[0-9a-fA-F]{40}$/.test(value) && !/^0x0{40}$/.test(value);
}

function shorten(address: string): string {
  return `${address.slice(0, 6)}…${address.slice(-4)}`;
}

export function ContractAddress() {
  const address = (__LEGATE_TOKEN_ADDRESS__ ?? '').trim();
  const [copied, setCopied] = useState(false);

  const copy = useCallback(() => {
    if (!navigator.clipboard) return;
    void navigator.clipboard.writeText(address).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    });
  }, [address]);

  if (!isAddress(address)) return null;

  return (
    <div className="mt-6 sm:mt-7 flex items-center gap-2.5 text-[11px] sm:text-xs">
      {/*
        One pill, not a row of parts: the address is long enough that splitting
        the label from the value makes the hero look like a form. Pressing it
        copies, and the pill says so rather than relying on an icon nobody reads.
      */}
      <button
        type="button"
        onClick={copy}
        title={address}
        aria-label={copied ? 'Contract address copied' : `Copy the $LEGATE contract address ${address}`}
        className="group inline-flex items-center gap-2.5 rounded-full border border-hairline hover:border-ink/30 bg-paper/80 backdrop-blur-sm pl-3 pr-3.5 py-1.5 transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
      >
        <span className="uppercase tracking-[0.16em] text-[10px] font-medium text-ink/45 group-hover:text-ink/70 transition-colors duration-200">
          {copied ? 'Copied' : 'Copy CA'}
        </span>
        <span aria-hidden="true" className="h-3 w-px bg-hairline" />
        <span className="font-mono text-ink/70 group-hover:text-ink transition-colors duration-200">
          {/* The full address where there is room; nobody can verify a truncation. */}
          <span className="hidden lg:inline">{address}</span>
          <span className="lg:hidden">{shorten(address)}</span>
        </span>
      </button>

      <a
        href={`${EXPLORER}/token/${address}`}
        target="_blank"
        rel="noreferrer noopener"
        className="text-ink/45 hover:text-accent transition-colors duration-200 underline-offset-4 hover:underline"
      >
        Explorer
      </a>
    </div>
  );
}

export default ContractAddress;
